import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { DecisionsBadge, isDecisionsModel } from '@/components/DecisionsBadge';
import { Dialog } from '@/components/Dialog';
import {
  AcceptsChips, CapabilitySwitches, apiErrorMessage, badNumber, discoverSuppliers, parseOptional, parsePrice,
  type Capabilities, type ModelPrefill
} from '@/components/modelBits';
import { fmtNumber, fmtUsd } from '@/lib/format';
import type { AdminModel, AvailableSupplierModel, ModelKind } from '@/types';

type Source = 'supplier' | 'openrouter' | 'manual';
type Step = 1 | 2 | 3;

const sources: { value: Source; title: string; desc: string }[] = [
  { value: 'supplier', title: 'From a supplier', desc: 'List what Anthropic, OpenAI and the others offer' },
  { value: 'openrouter', title: 'OpenRouter slug', desc: 'Paste one vendor/model slug' },
  { value: 'manual', title: 'Enter manually', desc: 'Type in the ID and prices yourself' }
];

function toPrefill(supplier: string, m: AvailableSupplierModel): ModelPrefill {
  return {
    supplier,
    modelId: m.modelId,
    displayName: m.displayName ?? m.modelId,
    kind: m.kind,
    maxContextTokens: m.maxContextTokens,
    inputCostPerMillionUsd: m.inputCostPerMillionUsd,
    outputCostPerMillionUsd: m.outputCostPerMillionUsd,
    cacheReadCostPerMillionUsd: m.cacheReadCostPerMillionUsd,
    cacheWriteCostPerMillionUsd: m.cacheWriteCostPerMillionUsd,
    priceSource: m.priceSource,
    supportsImageInput: m.supportsImageInput,
    supportsDocumentInput: m.supportsDocumentInput,
    supportsFileTasks: m.supportsFileTasks,
    supportsEffort: m.supportsEffort
  };
}

/**
 * Add models, in three steps: pick a source (a supplier's live list, one OpenRouter slug, or by
 * hand), find the model, then check the prefilled details and add it. Everything the old Discover,
 * OpenRouter lookup and Add model cards did, in one place.
 */
export function AddModelsDialog({ supplierOptions, onClose }: { supplierOptions: string[]; onClose: () => void }) {
  const [step, setStep] = useState<Step>(1);
  const [source, setSource] = useState<Source | null>(null);
  const [picked, setPicked] = useState<ModelPrefill | null>(null);
  // Bumped whenever a new pick should reset the review form.
  const [formKey, setFormKey] = useState(0);
  const [added, setAdded] = useState<AdminModel | null>(null);

  // Each step swaps the dialog's contents, so focus would otherwise fall back to the page. Move it
  // to the new step's first field instead.
  useEffect(() => {
    const id = requestAnimationFrame(() =>
      document.querySelector<HTMLElement>('.dialog [data-autofocus]')?.focus());
    return () => cancelAnimationFrame(id);
  }, [step, added]);

  const chooseSource = (s: Source) => {
    setSource(s);
    setPicked(null);
    if (s === 'manual') { setFormKey(k => k + 1); setStep(3); } else setStep(2);
  };

  const back = () => {
    if (step === 3) setStep(source === 'manual' ? 1 : 2);
    else setStep(1);
  };

  const toReview = () => { setFormKey(k => k + 1); setStep(3); };

  const startOver = () => { setAdded(null); setPicked(null); setSource(null); setStep(1); };

  const stepState = (n: Step) => (n < step ? 'done' : n === step ? 'cur' : '');
  const stepNames: Record<Step, string> = { 1: 'Source', 2: 'Find the model', 3: 'Check and add' };

  const steps = (
    <ol className="steps" aria-label="Progress">
      {([1, 2, 3] as Step[]).map((n, i) => (
        <li key={n} style={{ display: 'contents' }}>
          {i > 0 && <span className="step-line" aria-hidden="true" />}
          <span className={`step ${stepState(n)}`} aria-current={n === step ? 'step' : undefined}>
            <span className="dot" aria-hidden="true">{n < step ? '✓' : n}</span>
            {stepNames[n]}
            {n < step && <span className="sr-only"> (done)</span>}
            {source === 'manual' && n === 2 && <span className="sr-only"> (skipped)</span>}
          </span>
        </li>
      ))}
    </ol>
  );

  if (added) {
    return (
      <Dialog title="Add models" onClose={onClose}
        footer={
          <>
            <button type="button" className="btn-ghost" onClick={startOver}>Add another</button>
            <span className="spacer" />
            <Link to={`/admin/models/${added.id}`} className="btn-secondary" onClick={onClose}>Open its page</Link>
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </>
        }>
        <div className="info-banner" role="status" style={{ marginBottom: 0 }}>
          <strong>{added.displayName}</strong> was added to the catalogue{added.enabled ? ' and is enabled for callers' : ' (disabled)'}.
          Set a fallback, long-context pricing or other details on its page.
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Add models" onClose={onClose} size="lg" headerExtra={steps}
      footer={step === 3 ? undefined : (
        <>
          {step > 1 && <button type="button" className="btn-ghost" onClick={back}>Back</button>}
          <span className="spacer" />
          {step === 2 && picked?.priceSource && (
            <span className="dialog-foot-note">Prices are {picked.priceSource}. You can change them next.</span>
          )}
          {step === 2 && (
            <button type="button" className="btn-primary" disabled={!picked} onClick={toReview}>
              Next: check and add
            </button>
          )}
          {step === 1 && <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>}
        </>
      )}>
      {step < 3 && (
        <div className="src-grid" role="group" aria-label="Where the model comes from">
          {sources.map(s => (
            <button key={s.value} type="button" className={source === s.value ? 'src on' : 'src'}
              aria-pressed={source === s.value} onClick={() => chooseSource(s.value)}
              data-autofocus={s.value === 'supplier' && step === 1 ? true : undefined}>
              <span className="t">{s.title}</span>
              <span className="d">{s.desc}</span>
            </button>
          ))}
        </div>
      )}

      {step === 1 && (
        <p className="text-muted" style={{ fontSize: '.9rem' }}>
          Pick where the model comes from. Supplier lists and OpenRouter lookups prefill the price,
          context window and capabilities for you to check before adding.
        </p>
      )}

      {step === 2 && source === 'supplier' && <FromSupplier picked={picked} onPick={setPicked} />}
      {step === 2 && source === 'openrouter' && <FromOpenRouter onPick={setPicked} />}

      {step === 3 && (
        <ReviewForm
          key={formKey}
          supplierOptions={supplierOptions}
          initial={picked ?? undefined}
          onBack={back}
          onAdded={setAdded}
        />
      )}
    </Dialog>
  );
}

function FromSupplier({ picked, onPick }: { picked: ModelPrefill | null; onPick: (m: ModelPrefill | null) => void }) {
  const api = useApi();
  const [supplier, setSupplier] = useState('');
  const fetchModels = useMutation<AvailableSupplierModel[], unknown, string>({
    mutationFn: s => api.adminAvailableModels(s),
    onMutate: () => onPick(null)
  });
  const fetchedFor = fetchModels.variables;
  const results = fetchModels.data;

  return (
    <>
      <div className="lookup-row">
        <div className="field">
          <label htmlFor="discover-supplier">Supplier</label>
          <select id="discover-supplier" data-autofocus value={supplier} onChange={e => setSupplier(e.target.value)}>
            <option value="">Select a supplier...</option>
            {discoverSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button type="button" className="btn-secondary" onClick={() => fetchModels.mutate(supplier)}
          disabled={supplier === '' || fetchModels.isPending}>
          {fetchModels.isPending ? 'Fetching...' : 'List models'}
        </button>
      </div>
      {fetchModels.isError && (
        <div className="error-banner" role="alert" style={{ marginTop: 12 }}>{apiErrorMessage(fetchModels.error)}</div>
      )}
      {results && !fetchModels.isError && (
        results.length === 0 ? (
          <div className="placeholder">{fetchedFor} reported no models.</div>
        ) : (
          <>
            <div className="pick-list" role="list" aria-label={`Models from ${fetchedFor}`}>
              {results.map(m => {
                const on = picked?.modelId === m.modelId && picked.supplier === fetchedFor;
                return (
                  <div key={m.modelId} role="listitem" className={on ? 'pick-item on' : 'pick-item'}>
                    <div className="pick-main">
                      <div className="pick-name">{m.displayName ?? m.modelId}</div>
                      <div className="pick-meta">
                        <span className="mono">{m.modelId}</span>
                        {m.maxContextTokens ? ` · ${fmtNumber(m.maxContextTokens)} context` : ''}
                        {m.inputCostPerMillionUsd != null ? ` · ${fmtUsd(m.inputCostPerMillionUsd)} in` : ''}
                        {m.outputCostPerMillionUsd != null ? ` / ${fmtUsd(m.outputCostPerMillionUsd)} out per 1M` : ''}
                      </div>
                      <div style={{ marginTop: 3 }}>
                        <AcceptsChips images={m.supportsImageInput} documents={m.supportsDocumentInput}
                          fileTasks={m.supportsFileTasks} effort={m.supportsEffort} />
                      </div>
                    </div>
                    {m.inCatalog ? (
                      <span className="pill pill-disabled pill-sm">In catalogue</span>
                    ) : (
                      <button type="button" className={on ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                        aria-pressed={on}
                        onClick={() => onPick(on ? null : toPrefill(fetchedFor ?? supplier, m))}>
                        {on ? 'Selected' : 'Select'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-muted" style={{ fontSize: '.8rem', marginTop: 8 }}>
              Context comes from the supplier where reported; prices and capabilities are OpenRouter-derived —
              verify against the supplier's docs before heavy use.
            </p>
          </>
        )
      )}
    </>
  );
}

/** OpenRouter isn't bulk-listed (300+ models): resolve one slug at a time for its price. */
function FromOpenRouter({ onPick }: { onPick: (m: ModelPrefill | null) => void }) {
  const api = useApi();
  const [slug, setSlug] = useState('');
  const lookup = useMutation<AvailableSupplierModel, unknown, string>({
    mutationFn: s => api.adminResolveOpenRouterModel(s.trim()),
    onMutate: () => onPick(null),
    onSuccess: m => onPick(m.inCatalog ? null : toPrefill('OpenRouter', m))
  });
  const m = lookup.data;
  const decisions = !!m && isDecisionsModel(m);

  return (
    <>
      <form className="lookup-row" onSubmit={e => { e.preventDefault(); if (slug.includes('/')) lookup.mutate(slug); }}>
        <div className="field">
          <label htmlFor="or-slug">OpenRouter slug</label>
          <input id="or-slug" data-autofocus type="text" placeholder="e.g. typesafe/jev-1.13 or anthropic/claude-3.5-sonnet"
            value={slug} onChange={e => setSlug(e.target.value)} aria-describedby="or-slug-hint" />
        </div>
        <button type="submit" className="btn-secondary"
          disabled={slug.trim() === '' || !slug.includes('/') || lookup.isPending}>
          {lookup.isPending ? 'Looking up...' : 'Look up'}
        </button>
      </form>
      <div id="or-slug-hint" className="text-muted" style={{ fontSize: '.84rem', marginTop: 6 }}>
        Find slugs at <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer">openrouter.ai/models</a>.
        Language and Decisions models both work.
      </div>

      {lookup.isError && (
        <div className="error-banner" role="alert" style={{ marginTop: 12 }}>{apiErrorMessage(lookup.error)}</div>
      )}

      {m && !lookup.isError && (
        <div role="status">
          {decisions && (
            <div className="note-violet" style={{ marginTop: 12 }}>
              <div>
                <strong>This is a Decisions model, not an LLM.</strong> It answers typed questions — a choice, a
                yes/no probability or a score — with probabilities, instead of generating text. Callers reach it on{' '}
                <code>POST /api/v1/decisions</code>; it's never offered for chat and completions refuse it. Billed on
                input tokens only.
              </div>
            </div>
          )}
          <div className="preview-card">
            <div className="preview-title">
              {m.displayName ?? m.modelId}
              {decisions ? <DecisionsBadge /> : <span className="pill pill-kind pill-sm">LLM</span>}
              {m.inCatalog && <span className="pill pill-disabled pill-sm">Already in catalogue</span>}
            </div>
            <div className="kv"><span>Model ID to save</span><span className="mono">{m.modelId}</span></div>
            <div className="kv">
              <span>Input price</span>
              <span>{m.inputCostPerMillionUsd != null ? `${fmtUsd(m.inputCostPerMillionUsd)} per 1M tokens` : 'Unknown'}</span>
            </div>
            <div className="kv">
              <span>Output</span>
              <span>
                {decisions
                  ? 'Free, not billed'
                  : m.outputCostPerMillionUsd != null ? `${fmtUsd(m.outputCostPerMillionUsd)} per 1M tokens` : 'Unknown'}
              </span>
            </div>
            <div className="kv">
              <span>{decisions ? 'Input limit' : 'Context window'}</span>
              <span>{m.maxContextTokens ? `${fmtNumber(m.maxContextTokens)} tokens` : 'Unknown'}</span>
            </div>
            {m.inCatalog && (
              <p className="text-muted" style={{ fontSize: '.84rem', marginTop: 8 }}>
                This model is already in the catalogue — open it from the Models list to change it.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Step 3: the prefilled details, editable, with the same rules the old Add model card applied —
 * supplier, model ID, display name, input and output price required; every number non-negative.
 */
function ReviewForm({ supplierOptions, initial, onBack, onAdded }: {
  supplierOptions: string[];
  initial?: ModelPrefill;
  onBack: () => void;
  onAdded: (m: AdminModel) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const str = (v: number | null | undefined) => (v != null ? String(v) : '');
  const [supplier, setSupplier] = useState(initial?.supplier ?? '');
  const [modelId, setModelId] = useState(initial?.modelId ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const kind: ModelKind = initial?.kind ?? 'Llm';
  // Decisions is sent only while it's still the OpenRouter slug that was looked up.
  const decisions = kind === 'Decisions' && supplier === 'OpenRouter' && modelId.trim() === initial?.modelId;
  const [input, setInput] = useState(str(initial?.inputCostPerMillionUsd));
  // Decisions models bill input only, so output is 0 rather than an unknown to fill in.
  const [output, setOutput] = useState(
    initial?.outputCostPerMillionUsd != null ? String(initial.outputCostPerMillionUsd) : kind === 'Decisions' ? '0' : '');
  const [cacheRead, setCacheRead] = useState(str(initial?.cacheReadCostPerMillionUsd));
  const [cacheWrite, setCacheWrite] = useState(str(initial?.cacheWriteCostPerMillionUsd));
  const [maxContext, setMaxContext] = useState(str(initial?.maxContextTokens));
  const [enabled, setEnabled] = useState(true);
  const [caps, setCaps] = useState<Capabilities>({
    images: initial?.supportsImageInput ?? false,
    documents: initial?.supportsDocumentInput ?? false,
    fileTasks: initial?.supportsFileTasks ?? false,
    effort: initial?.supportsEffort ?? false,
    // Permissive by default — the models that refuse the combination are the exception.
    effortWithTools: true
  });

  const create = useMutation({
    mutationFn: () =>
      api.adminUpsertModel({
        supplier,
        modelId: modelId.trim(),
        displayName: displayName.trim(),
        enabled,
        kind: decisions ? 'Decisions' : undefined,
        inputCostPerMillionUsd: parsePrice(input),
        outputCostPerMillionUsd: parsePrice(output),
        cacheReadCostPerMillionUsd: parseOptional(cacheRead),
        cacheWriteCostPerMillionUsd: parseOptional(cacheWrite),
        maxContextTokens: maxContext.trim() === '' ? null : Number(maxContext),
        supportsImageInput: caps.images,
        supportsDocumentInput: caps.documents,
        supportsFileTasks: caps.fileTasks,
        supportsEffort: caps.effort,
        supportsEffortWithTools: caps.effortWithTools
      }),
    onSuccess: m => {
      qc.invalidateQueries({ queryKey: ['admin-models'] });
      onAdded(m);
    }
  });

  const anyInvalid = [input, output, cacheRead, cacheWrite, maxContext].some(badNumber);
  const missing = [
    supplier === '' ? 'supplier' : null,
    modelId.trim() === '' ? 'model ID' : null,
    displayName.trim() === '' ? 'display name' : null,
    input.trim() === '' ? 'input price' : null,
    output.trim() === '' ? 'output price' : null
  ].filter(Boolean);
  const canSubmit = missing.length === 0 && !anyInvalid && !create.isPending;

  const options = useMemo(() => {
    const names = new Set(supplierOptions);
    if (initial?.supplier) names.add(initial.supplier);
    return [...names].sort();
  }, [supplierOptions, initial]);

  const numField = (id: string, label: string, value: string, set: (v: string) => void, opts?: { step?: number; placeholder?: string; hint?: string }) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" min={0} step={opts?.step ?? 0.01} placeholder={opts?.placeholder} value={value}
        onChange={e => set(e.target.value)} aria-invalid={badNumber(value) || undefined} />
      {badNumber(value) && <div className="field-error">Enter a number of 0 or more.</div>}
      {opts?.hint && <div className="hint readable">{opts.hint}</div>}
    </div>
  );

  return (
    <form onSubmit={e => { e.preventDefault(); if (canSubmit) create.mutate(); }}>
      {initial?.priceSource && (
        <div className="warn-text" style={{ marginBottom: 12 }}>
          Prices prefilled from {initial.priceSource} — verify against the supplier's pricing page.
        </div>
      )}
      {decisions && (
        <div className="note-violet" style={{ marginBottom: 14 }}>
          <DecisionsBadge />
          <span>Callers reach this model on <code>POST /api/v1/decisions</code>. Billed on input tokens only.</span>
        </div>
      )}
      {create.error && <div className="error-banner" role="alert">Could not add the model: {apiErrorMessage(create.error)}</div>}

      <div className="field-row">
        <div className="field">
          <label htmlFor="new-supplier">Supplier</label>
          <select id="new-supplier" value={supplier} onChange={e => setSupplier(e.target.value)} data-autofocus>
            <option value="">Select a supplier...</option>
            {options.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="new-model-id">Model ID</label>
          <input id="new-model-id" type="text" className="mono"
            placeholder={supplier === 'OpenRouter' ? 'e.g. anthropic/claude-3.5-sonnet' : 'e.g. claude-sonnet-4-5'}
            value={modelId} onChange={e => setModelId(e.target.value)} />
          {supplier === 'OpenRouter' && (
            <div className="hint readable">Use the full OpenRouter slug (vendor/model).</div>
          )}
          {kind === 'Decisions' && !decisions && (
            <div className="warn-text" style={{ marginTop: 4 }}>
              Changed from the looked-up slug, so this will be added as an ordinary LLM, not a Decisions model.
            </div>
          )}
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="new-display">Display name</label>
          <input id="new-display" type="text" placeholder="e.g. Claude Sonnet 4.5" value={displayName}
            onChange={e => setDisplayName(e.target.value)} />
        </div>
        {numField('new-context', decisions ? 'Input limit (tokens)' : 'Max context tokens', maxContext, setMaxContext,
          { step: 1, placeholder: 'Optional' })}
      </div>
      <div className="field-row">
        {numField('new-input', 'Input cost $/1M', input, setInput)}
        {decisions
          ? <div className="field"><label>Output cost</label><div style={{ padding: '8px 0' }}>Free, not billed</div></div>
          : numField('new-output', 'Output cost $/1M', output, setOutput)}
      </div>
      {!decisions && (
        <div className="field-row">
          {numField('new-cache-read', 'Cache read cost $/1M', cacheRead, setCacheRead, { placeholder: 'Optional' })}
          {numField('new-cache-write', 'Cache write cost $/1M', cacheWrite, setCacheWrite, { placeholder: 'Optional' })}
        </div>
      )}

      <div className="field">
        <label>What it accepts</label>
        {decisions ? (
          <div className="text-muted" style={{ fontSize: '.88rem' }}>
            Text and JSON{caps.images ? ', plus images' : ''}. Tools, effort, PDFs and file tasks don't apply to Decisions models.
          </div>
        ) : (
          <>
            <CapabilitySwitches value={caps} onChange={setCaps} idPrefix="new-cap" />
            {initial?.priceSource && (
              <div className="hint readable">Prefilled from OpenRouter where known — verify against the supplier's docs.</div>
            )}
          </>
        )}
      </div>

      <div className="field">
        <label className="switch-label">
          <span className="switch">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <span className="track"></span>
          </span>
          Enabled for callers
        </label>
      </div>

      <div className="dialog-foot" style={{ margin: '8px -20px -18px', position: 'sticky', bottom: -18 }}>
        <button type="button" className="btn-ghost" onClick={onBack}>Back</button>
        <span className="spacer" />
        {missing.length > 0 && <span className="dialog-foot-note">Still needed: {missing.join(', ')}</span>}
        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {create.isPending ? 'Adding...' : 'Add model'}
        </button>
      </div>
    </form>
  );
}
